// src/exam/pages/ExamJoin.jsx
import { useEffect, useMemo, useState } from "react"
import { motion } from "framer-motion"
import { Trophy, Clock, BookOpen, User, Play, AlertTriangle, Palette, CheckCircle2, ChevronLeft, ChevronRight } from "lucide-react"
import { useExamApi } from "../context"
import { presets } from "../themePresets"

const fade = { initial: { opacity: 0, y: 12 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.3 } }

export default function ExamJoin({ onStart }) {
  const { api, theme, setTheme, t } = useExamApi()
  const [loading, setLoading] = useState(true)
  const [err, setErr] = useState("")
  const [detail, setDetail] = useState(null)
  const [nick, setNick] = useState("")
  const [themeOpen, setThemeOpen] = useState(false)
  const [collapsed, setCollapsed] = useState(sessionStorage.getItem("kuizy_theme_collapsed") === "1")
  const applyTheme = v => { setTheme(v); localStorage.setItem("kuizy_theme", v); setThemeOpen(false) }
  const toggleCollapse = () => { const v = !collapsed; setCollapsed(v); sessionStorage.setItem("kuizy_theme_collapsed", v ? "1" : "0"); if (v) setThemeOpen(false) }

  useEffect(() => {
    setLoading(true)
    setErr("")
    api.getOverview()
      .then(d => { setDetail(d); setLoading(false) })
      .catch(() => { setErr("ไม่สามารถโหลดข้อมูลได้"); setLoading(false) })
  }, [api])

  const tmeta = detail?.test || {}
  const windowOk = detail?.status ? detail.status === "ok" : true
  const statusMsg = useMemo(() => {
    if (!detail?.status) return ""
    if (detail.status === "ok") return ""
    if (detail.status === "inactive_or_deleted") return "รอบสอบถูกปิดใช้งาน/ถูกลบ หรือชุดข้อสอบถูกปิด"
    if (detail.status === "out_of_window") return "อยู่นอกช่วงเวลาเปิดสอบ"
    if (detail.status === "not_found") return "ไม่พบรอบสอบ"
    return "ไม่สามารถเริ่มทำข้อสอบได้"
  }, [detail])

  const requireNick = api.kind === "public"
  const canStart = !loading && windowOk && (!requireNick || nick.trim().length > 0)

  return (
    <div className={`min-h-screen flex items-center justify-center p-6 ${t.bgGrad}`}>
      <div className="fixed top-3 right-3 z-50">
        {collapsed ? (
          <button onClick={toggleCollapse} className={`h-10 w-10 rounded-full shadow ${t.neutralBtn} ${t.border} border grid place-items-center`}>
            <ChevronLeft className="size-5" />
          </button>
        ) : (
          <div className="flex items-center gap-2">
            <button onClick={toggleCollapse} className={`h-10 w-10 rounded-full shadow ${t.neutralBtn} ${t.border} border grid place-items-center`}>
              <ChevronRight className="size-5" />
            </button>
            <div className="relative">
              <button onClick={() => setThemeOpen(s => !s)} className={`h-10 w-10 rounded-full shadow ${t.neutralBtn} ${t.border} border grid place-items-center`}>
                <Palette className="size-5" />
              </button>
              {themeOpen && (
                <div className={`absolute right-0 mt-2 w-44 rounded-xl p-2 backdrop-blur ${t.card} ${t.text}`}>
                  <div className={`text-xs mb-2 px-1 opacity-80 ${t.subtextStrong}`}>ธีม</div>
                  {Object.keys(presets).map(k => (
                    <button key={k} onClick={() => applyTheme(k)} className={`w-full h-9 px-3 rounded-lg flex items-center justify-between ${t.soft}`}>
                      <span>{k}</span>{theme === k ? <CheckCircle2 className="size-4 text-primary" /> : null}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      <div className="w-full max-w-xl">
        <img src="/images/app-logo.png" alt="Kuizy" className="h-28 mx-auto" />
        <motion.div {...fade} className={`mt-4 w-full backdrop-blur rounded-2xl p-6 shadow-2xl ${t.card}`}>
          <div className="flex items-center gap-3">
            <div className="size-10 rounded-xl bg-primary/20 flex items-center justify-center"><Trophy className="size-5 text-primary" /></div>
            <div className={`text-xl font-semibold ${t.text}`}>{tmeta?.name || "Exam"}</div>
          </div>

          <div className="mt-5 grid grid-cols-3 gap-3">
            <div className={`rounded-xl p-4 ${t.soft}`}>
              <div className={`text-xs ${t.subtext}`}>จำนวนข้อ</div>
              <div className={`text-lg ${t.text}`}>{tmeta?.question_count ?? "-"}</div>
            </div>
            <div className={`rounded-xl p-4 ${t.soft}`}>
              <div className={`text-xs ${t.subtext}`}>เวลา</div>
              <div className={`text-lg ${t.text}`}><Clock className="inline size-4 mr-1" />{tmeta?.duration ? `${tmeta.duration} นาที` : "ไม่จำกัด"}</div>
            </div>
            <div className={`rounded-xl p-4 ${t.soft}`}>
              <div className={`text-xs ${t.subtext}`}>เกณฑ์ผ่าน</div>
              <div className={`text-lg ${t.text}`}>{tmeta?.criteria ? `${tmeta.criteria}%` : "-"}</div>
            </div>
          </div>

          {tmeta?.content_code || tmeta?.content_name ? (
            <div className={`mt-5 rounded-xl p-4 flex items-center gap-3 ${t.soft}`}>
              <div className="size-9 rounded-lg bg-secondary/20 flex items-center justify-center"><BookOpen className="size-5 text-secondary" /></div>
              <div className={`${t.text} text-sm`}>{tmeta?.content_code ? `${tmeta.content_code} • ${tmeta?.content_name || ""}` : (tmeta?.content_name || "")}</div>
            </div>
          ) : null}

          {(!windowOk || err) && (
            <div className="mt-4 rounded-xl border border-yellow-400/30 bg-yellow-500/10 text-yellow-700 px-4 py-3 text-sm flex items-start gap-2">
              <AlertTriangle className="size-4 mt-0.5" />
              <div>{statusMsg || err}</div>
            </div>
          )}

          <div className="mt-6">
            {requireNick ? (
              <>
                <div className={`text-sm mb-2 ${t.subtextStrong}`}>ใส่ชื่อที่จะใช้ขึ้นตารางคะแนน</div>
                <div className="flex gap-2">
                  <div className="flex-1 relative">
                    <User className="size-4 opacity-70 absolute left-3 top-1/2 -translate-y-1/2" />
                    <input
                      value={nick}
                      onChange={e => setNick(e.target.value)}
                      className={`w-full rounded-xl pl-9 pr-3 h-11 outline-none border focus:border-primary/60 ${t.inputBg} ${t.inputText} ${t.inputBorder}`}
                      placeholder="เช่น เจนวิทย์@อุ่นใจ"
                    />
                  </div>
                  <button
                    onClick={() => { sessionStorage.setItem("player_nick", nick); onStart() }}
                    disabled={!canStart}
                    className="h-11 px-5 rounded-xl bg-primary text-white flex items-center gap-2 disabled:opacity-50"
                  >
                    <Play className="size-4" /> เริ่มสอบ
                  </button>
                </div>
              </>
            ) : (
              <div className="flex justify-end">
                <button
                  onClick={onStart}
                  disabled={!canStart}
                  className="h-11 px-5 rounded-xl bg-primary text-white flex items-center gap-2 disabled:opacity-50"
                >
                  <Play className="size-4" /> เริ่มสอบ
                </button>
              </div>
            )}
          </div>
        </motion.div>
      </div>
    </div>
  )
}
